import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { coursesAPI, enrollmentsAPI, categoriesAPI } from '../lib/api';
import { toast } from 'sonner';
import CertificatePreviewModal from '../components/CertificatePreviewModal';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  Search,
  Clock,
  Users,
  ChevronRight,
  Sparkles,
  BookOpen,
  ShieldCheck,
  Package,
  TrendingUp,
  Calendar,
  UserPlus,
  Filter,
  Award,
  Brain,
  Loader2,
  Star,
  Target,
  Zap,
  HelpCircle,
  Play,
  CheckCircle2,
} from 'lucide-react';

const categoryIcons = {
  onboarding: UserPlus,
  product: Package,
  sales: TrendingUp,
  compliance: ShieldCheck,
  seasonal: Calendar,
  leadership: Users,
};

export const Courses = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { hasFeature, isPlatformUser, edition } = useTenant();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [enrolling, setEnrolling] = useState(null);
  const [certificateModal, setCertificateModal] = useState({ open: false, enrollment: null, courseName: '' });
  const [sortBy, setSortBy] = useState('latest_assigned'); // Sorting state
  const [quizAssignments, setQuizAssignments] = useState([]); // Quiz assignments
  
  // State for storing translated courses keyed by `${courseId}_${lang}`
  const [courseTranslations, setCourseTranslations] = useState({});
  const translatedRef = useRef(new Set());

  // Helper to get translated course text
  const getCourseText = (course, field) => {
    const lang = i18n.language;
    if (lang === 'en') return course[field];
    if (course.translations?.[lang]?.[field]) return course.translations[lang][field];
    const key = `${course.id}_${lang}`;
    if (courseTranslations[key]?.[field]) return courseTranslations[key][field];
    return course[field];
  };
  
  // AI Recommendations - only available for non-standard editions
  const canUseAIRecommendations = isPlatformUser || hasFeature('ai_recommendations') || edition !== 'standard';
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationAnalysis, setRecommendationAnalysis] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [coursesRes, enrollmentsRes, categoriesRes, quizAssignmentsRes] = await Promise.all([
          coursesAPI.getAll(),
          enrollmentsAPI.getAll(),
          categoriesAPI.getAll(),
          axios.get(`${API_URL}/my-quiz-assignments`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { assignments: [] } }))
        ]);
        setCourses(coursesRes.data?.courses || coursesRes.data || []);
        setEnrollments(enrollmentsRes.data);
        setCategories(categoriesRes.data);
        setQuizAssignments(quizAssignmentsRes.data?.assignments || []);
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Trigger translation when courses load and language is not English
  useEffect(() => {
    const lang = i18n.language;
    if (lang === 'en' || courses.length === 0) return;

    const toTranslate = courses.filter(c => !c.translations?.[lang] && !translatedRef.current.has(`${c.id}_${lang}`));
    if (toTranslate.length === 0) return;

    let cancelled = false;

    const translateInBatches = async () => {
      const token = localStorage.getItem('token');
      const batchSize = 3; // 3 courses per batch = 6 texts per API call

      for (let i = 0; i < toTranslate.length; i += batchSize) {
        if (cancelled) break;
        const batch = toTranslate.slice(i, i + batchSize);
        const texts = batch.flatMap(c => [c.title, c.description || '']);

        try {
          const response = await axios.post(
            `${API_URL}/translate-text`,
            { texts, target_language: lang },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!cancelled && response.data?.translated_texts) {
            const newT = {};
            batch.forEach((c, idx) => {
              translatedRef.current.add(`${c.id}_${lang}`);
              newT[`${c.id}_${lang}`] = {
                title: response.data.translated_texts[idx * 2] || c.title,
                description: response.data.translated_texts[idx * 2 + 1] || c.description
              };
            });
            setCourseTranslations(prev => ({ ...prev, ...newT }));
          }
        } catch (err) {
          console.error('Course translation batch error:', err);
        }
      }
    };

    translateInBatches();
    return () => { cancelled = true; };
  }, [courses, i18n.language]);

  // Fetch AI recommendations
  const fetchAiRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ai/recommendations/top-performers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { user_id: user?.id }
      });
      setAiRecommendations(response.data.recommendations || []);
      setRecommendationAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Failed to fetch AI recommendations:', error);
      toast.error('Failed to load AI recommendations');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrolling(courseId);
    try {
      const response = await enrollmentsAPI.create({ course_id: courseId });
      setEnrollments([...enrollments, response.data]);
      toast.success('Successfully enrolled in course!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(null);
    }
  };

  const enrolledCourseIds = enrollments.map(e => e.course_id);
  
  const filteredCourses = courses.filter(course => {
    const matchesSearch = getCourseText(course, 'title').toLowerCase().includes(search.toLowerCase()) ||
      (getCourseText(course, 'description') || '').toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || course.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // Convert quiz assignments to display items
  const quizItems = quizAssignments
    .filter(q => {
      const matchesSearch = q.quiz_title?.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    })
    .map(q => ({
      ...q,
      id: q.quiz_id,
      title: q.quiz_title,
      _isQuiz: true,
      description: 'Standalone Quiz',
      category: 'Assessment',
      assigned_at: q.assigned_at,
      created_at: q.assigned_at,
    }));

  // Combine enrolled courses with quiz assignments
  const myCoursesRaw = [
    ...filteredCourses.filter(c => enrolledCourseIds.includes(c.id)).map(c => {
      const enrollment = enrollments.find(e => e.course_id === c.id);
      return {
        ...c,
        _isQuiz: false,
        assigned_at: enrollment?.assigned_at || enrollment?.enrolled_at || c.created_at,
      };
    }),
    ...quizItems
  ];

  // Apply sorting to combined list
  const sortContent = (items) => {
    return [...items].sort((a, b) => {
      switch (sortBy) {
        case 'latest_assigned':
          return new Date(b.assigned_at || b.created_at || 0) - new Date(a.assigned_at || a.created_at || 0);
        case 'oldest_assigned':
          return new Date(a.assigned_at || a.created_at || 0) - new Date(b.assigned_at || b.created_at || 0);
        case 'latest_created':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'name_asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'name_desc':
          return (b.title || '').localeCompare(a.title || '');
        default:
          return new Date(b.assigned_at || b.created_at || 0) - new Date(a.assigned_at || a.created_at || 0);
      }
    });
  };

  const myCourses = sortContent(myCoursesRaw);
  const availableCourses = filteredCourses.filter(c => !enrolledCourseIds.includes(c.id));
  
  // Completed courses - only those with status = 'completed'
  const completedCourses = sortContent(filteredCourses.filter(c => {
    const enrollment = enrollments.find(e => e.course_id === c.id);
    return enrollment?.status === 'completed';
  }).map(c => {
    const enrollment = enrollments.find(e => e.course_id === c.id);
    return { ...c, assigned_at: enrollment?.completed_at || enrollment?.updated_at };
  }));
  
  // In progress courses (enrolled but not completed) - includes quizzes
  const inProgressCourses = myCourses.filter(c => {
    if (c._isQuiz) {
      return c.status !== 'completed';
    }
    const enrollment = enrollments.find(e => e.course_id === c.id);
    return enrollment?.status !== 'completed';
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full max-w-md" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-72" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const CourseCard = ({ course, isEnrolled }) => {
    const enrollment = enrollments.find(e => e.course_id === course.id);
    const CategoryIcon = course._isQuiz ? HelpCircle : (categoryIcons[course.category] || BookOpen);
    const isQuiz = course._isQuiz;
    
    // Check due date status - don't show overdue for completed courses
    const getDueDateStatus = () => {
      const dueDate = isQuiz ? course.due_date : enrollment?.due_date;
      if (!dueDate) return null;
      
      // Never show overdue for completed courses or courses with certificates
      const status = isQuiz ? course.status : enrollment?.status;
      if (status === 'completed' || enrollment?.has_certificate) return null;
      
      const dueDateObj = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((dueDateObj - today) / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return { text: 'Overdue', variant: 'destructive' };
      if (diffDays === 0) return { text: 'Due Today', variant: 'destructive' };
      if (diffDays === 1) return { text: 'Due Tomorrow', variant: 'warning' };
      if (diffDays <= 3) return { text: `Due in ${diffDays} days`, variant: 'warning' };
      return { text: `Due: ${dueDateObj.toLocaleDateString()}`, variant: 'outline' };
    };
    
    const dueDateStatus = isEnrolled ? getDueDateStatus() : null;

    const handleQuizClick = (e) => {
      if (isQuiz) {
        e.preventDefault();
        navigate(`/quiz/${course.quiz_id}`);
      }
    };

    const CardWrapper = isQuiz ? 'div' : Link;
    const cardProps = isQuiz 
      ? { onClick: handleQuizClick, className: "block cursor-pointer" }
      : { to: `/courses/${course.id}`, className: "block" };

    return (
      <CardWrapper {...cardProps}>
        <Card className={`group overflow-hidden card-hover cursor-pointer ${isQuiz ? 'border-purple-200 dark:border-purple-800' : ''}`} data-testid={isQuiz ? `quiz-card-${course.quiz_id}` : `course-card-${course.id}`}>
          <div className="aspect-video relative overflow-hidden">
            {isQuiz ? (
              <div className="w-full h-full bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
                <HelpCircle className="w-16 h-16 text-purple-500/40" />
              </div>
            ) : (
              <img
                src={course.thumbnail || course.thumbnail_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=400&auto=format&fit=crop'}
                alt={getCourseText(course, 'title')}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-3 left-3 flex gap-2">
              {isQuiz ? (
                <Badge variant="secondary" className="capitalize bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                  <HelpCircle className="w-3 h-3 mr-1" />
                  Quiz
                </Badge>
              ) : (
                <Badge variant="secondary" className="capitalize bg-background/80 backdrop-blur">
                  <CategoryIcon className="w-3 h-3 mr-1" />
                  {course.category}
                </Badge>
              )}
              {course.is_compliance && (
                <Badge variant="destructive">Required</Badge>
              )}
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="gold-gradient text-white border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                +{isQuiz ? 50 : course.points_reward}
              </Badge>
            </div>
            {isEnrolled && !isQuiz && enrollment && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${enrollment.progress}%` }}
                />
              </div>
            )}
            {isQuiz && course.status && (
              <div className="absolute bottom-3 right-3">
                <Badge 
                  variant={course.status === 'completed' ? 'default' : 'secondary'}
                  className={course.status === 'completed' ? 'bg-green-600' : ''}
                >
                  {course.status === 'completed' ? 'Completed' : course.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                </Badge>
              </div>
            )}
          </div>
          <CardContent className="p-4">
          <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors mb-2">
            {getCourseText(course, 'title')}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {getCourseText(course, 'description') || (isQuiz ? t('courses.standaloneQuiz', 'Standalone Assessment Quiz') : '')}
          </p>
          {/* Due Date Badge */}
          {dueDateStatus && (
            <div className="mb-3">
              <Badge 
                variant={dueDateStatus.variant === 'warning' ? 'outline' : dueDateStatus.variant}
                className={dueDateStatus.variant === 'warning' ? 'border-amber-500 text-amber-600 bg-amber-500/10' : ''}
              >
                <Calendar className="w-3 h-3 mr-1" />
                {dueDateStatus.text}
              </Badge>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {isQuiz ? '15' : course.duration_minutes} {t('common.minutes')}
              </span>
              {!isQuiz && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {course.enrolled_count}
                </span>
              )}
            </div>
            {isQuiz ? (
              course.status === 'completed' ? (
                <Button 
                  size="sm" 
                  className="rounded-full bg-green-600 hover:bg-green-700" 
                  data-testid={`quiz-completed-${course.quiz_id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/quiz/${course.quiz_id}`);
                  }}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Completed
                </Button>
              ) : (
                <Button 
                  size="sm" 
                  className="rounded-full bg-purple-600 hover:bg-purple-700" 
                  data-testid={`start-quiz-${course.quiz_id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/quiz/${course.quiz_id}`);
                  }}
                >
                  <Play className="w-4 h-4 mr-1" />
                  {course.status === 'in_progress' ? 'Continue' : 'Start Quiz'}
                </Button>
              )
            ) : isEnrolled ? (
              enrollment?.status === 'completed' ? (
                <Button 
                  size="sm" 
                  className="rounded-full bg-green-600 hover:bg-green-700" 
                  data-testid={`download-cert-${course.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCertificateModal({
                      open: true,
                      enrollment: enrollment,
                      courseName: getCourseText(course, 'title')
                    });
                  }}
                >
                  <Award className="w-4 h-4 mr-1" />
                  {t('courses.downloadCertificate', 'Certificate')}
                </Button>
              ) : (
                <Button asChild size="sm" className="rounded-full" data-testid={`continue-course-${course.id}`}>
                  <span onClick={(e) => e.stopPropagation()}>
                    {t('courses.continue')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </Button>
              )
            ) : (
              <Button
                size="sm"
                className="rounded-full"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEnroll(course.id);
                }}
                disabled={enrolling === course.id}
                data-testid={`enroll-course-${course.id}`}
              >
                {enrolling === course.id ? t('common.loading') : t('courses.enroll')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      </CardWrapper>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="courses-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{t('courses.allCourses')}</h1>
            <p className="text-muted-foreground">{t('courses.viewDetails')}</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="course-search"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-48" data-testid="sort-select">
              <SelectValue placeholder={t('common.sortBy', 'Sort by')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest_assigned">{t('courses.sorting.latestAssigned', 'Latest Assigned')}</SelectItem>
              <SelectItem value="oldest_assigned">{t('courses.sorting.oldestAssigned', 'Oldest Assigned')}</SelectItem>
              <SelectItem value="latest_created">{t('courses.sorting.latestCreated', 'Latest Created')}</SelectItem>
              <SelectItem value="name_asc">{t('courses.sorting.nameAZ', 'Name (A-Z)')}</SelectItem>
              <SelectItem value="name_desc">{t('courses.sorting.nameZA', 'Name (Z-A)')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Button
            variant={activeCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full flex-shrink-0"
            onClick={() => setActiveCategory('all')}
            data-testid="filter-all"
          >
            <Filter className="w-4 h-4 mr-1" />
            {t('courses.categories.all')}
          </Button>
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.id] || BookOpen;
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                className="rounded-full flex-shrink-0"
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {t(`courses.categories.${cat.id}`, cat.name)}
              </Button>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full" onValueChange={(value) => {
          if (value === 'ai-recommended' && canUseAIRecommendations && aiRecommendations.length === 0 && !loadingRecommendations) {
            fetchAiRecommendations();
          }
        }}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="all" data-testid="tab-all">
              {t('common.all')} ({filteredCourses.length + quizItems.length})
            </TabsTrigger>
            {canUseAIRecommendations && (
              <TabsTrigger value="ai-recommended" data-testid="tab-ai-recommended" className="bg-gradient-to-r from-purple-500/10 to-blue-500/10">
                <Brain className="w-4 h-4 mr-1" />
                {t('courses.aiRecommended', 'AI Recommended')}
              </TabsTrigger>
            )}
            <TabsTrigger value="in-progress" data-testid="tab-in-progress">
              {t('courses.inProgress', 'In Progress')} ({inProgressCourses.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              {t('courses.completed', 'Completed')} ({completedCourses.length})
            </TabsTrigger>
            <TabsTrigger value="available" data-testid="tab-available">
              {t('courses.available', 'Available')} ({availableCourses.length})
            </TabsTrigger>
          </TabsList>

          {/* AI Recommended Tab - Only render if user has AI features */}
          {canUseAIRecommendations && (
          <TabsContent value="ai-recommended" data-testid="ai-recommended-content">
            <div className="space-y-6">
              {/* AI Header */}
              <Card className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 border-purple-500/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-purple-500/20">
                      <Brain className="w-8 h-8 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-1">AI-Powered Recommendations</h3>
                      <p className="text-muted-foreground mb-3">
                        I am learning your patterns, will start recommending courses soon.
                      </p>
                      {recommendationAnalysis && (
                        <div className="flex flex-wrap gap-3 text-sm">
                          <Badge variant="outline" className="bg-background">
                            <Users className="w-3 h-3 mr-1" />
                            {recommendationAnalysis.total_top_performers_analyzed} Top Performers Analyzed
                          </Badge>
                          <Badge variant="outline" className="bg-background">
                            <BookOpen className="w-3 h-3 mr-1" />
                            {recommendationAnalysis.courses_analyzed} Courses Analyzed
                          </Badge>
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={fetchAiRecommendations}
                      disabled={loadingRecommendations}
                    >
                      {loadingRecommendations ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-1" />
                          Refresh
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Loading State */}
              {loadingRecommendations && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="overflow-hidden">
                      <Skeleton className="h-40" />
                      <CardContent className="p-4 space-y-3">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Recommendations */}
              {!loadingRecommendations && aiRecommendations.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {aiRecommendations.map((rec, index) => (
                    <Link to={`/courses/${rec.course_id}`} key={rec.course_id} className="block">
                      <Card className="group overflow-hidden card-hover cursor-pointer h-full border-2 hover:border-purple-500/50 transition-colors" data-testid={`ai-rec-${rec.course_id}`}>
                        <div className="aspect-video relative overflow-hidden bg-gradient-to-br from-purple-500/20 to-blue-500/20">
                          {rec.course?.thumbnail_url ? (
                            <img
                              src={rec.course.thumbnail_url}
                              alt={rec.course?.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-16 h-16 text-purple-500/40" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute top-3 left-3">
                            <Badge className="bg-purple-500 text-white border-0">
                              <Star className="w-3 h-3 mr-1" />
                              #{index + 1} Recommended
                            </Badge>
                          </div>
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-green-500 text-white border-0">
                              <Target className="w-3 h-3 mr-1" />
                              {rec.performance_correlation}% Match
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-purple-500 transition-colors mb-2">
                            {rec.course?.title}
                          </h3>
                          
                          {/* Why Recommended - AI Reason */}
                          <div className="bg-muted/50 rounded-lg p-3 mb-3">
                            <div className="flex items-start gap-2">
                              <Brain className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-purple-600 mb-1">Why this course?</p>
                                <p className="text-sm text-muted-foreground line-clamp-3">{rec.reason}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {rec.top_performers_completed}/{rec.total_top_performers} top performers
                            </span>
                            {rec.average_score > 0 && (
                              <span className="flex items-center gap-1">
                                <Award className="w-3 h-3" />
                                {rec.average_score}% avg score
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-3 flex items-center justify-between">
                            <Badge variant="outline">{rec.course?.category}</Badge>
                            {!enrolledCourseIds.includes(rec.course_id) && (
                              <Button
                                size="sm"
                                className="rounded-full bg-purple-500 hover:bg-purple-600"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEnroll(rec.course_id);
                                }}
                                disabled={enrolling === rec.course_id}
                              >
                                {enrolling === rec.course_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enroll Now'}
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}

              {/* No Recommendations */}
              {!loadingRecommendations && aiRecommendations.length === 0 && (
                <Card className="p-12 text-center">
                  <Brain className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Building Your Recommendations</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Our AI is analyzing learning patterns from top performers. 
                    As more data becomes available, personalized recommendations will appear here.
                  </p>
                  <Button variant="outline" className="mt-4" onClick={fetchAiRecommendations}>
                    <Zap className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>
          )}

          <TabsContent value="all">
            {(filteredCourses.length > 0 || quizItems.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Combine and sort all courses and quizzes */}
                {sortContent([...filteredCourses.map(c => ({...c, _isQuiz: false, assigned_at: enrollments.find(e => e.course_id === c.id)?.assigned_at || c.created_at})), ...quizItems]).map((item) => (
                  <CourseCard
                    key={item._isQuiz ? `quiz-${item.quiz_id}` : item.id}
                    course={item}
                    isEnrolled={item._isQuiz ? true : enrolledCourseIds.includes(item.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('courses.noCourses', 'No courses found')}</h3>
                <p className="text-muted-foreground">{t('courses.adjustFilters', 'Try adjusting your search or filters')}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="in-progress">
            {inProgressCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {inProgressCourses.map((course) => (
                  <CourseCard key={course.id} course={course} isEnrolled={true} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('courses.noInProgress', 'No courses in progress')}</h3>
                <p className="text-muted-foreground">{t('courses.startLearning', 'Browse available courses and start learning!')}</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="completed">
            {completedCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedCourses.map((course) => (
                  <CourseCard key={course.id} course={course} isEnrolled={true} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('courses.noCompleted', 'No completed courses yet')}</h3>
                <p className="text-muted-foreground">{t('courses.keepLearning', 'Complete your enrolled courses to see them here!')}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="available">
            {availableCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableCourses.map((course) => (
                  <CourseCard key={course.id} course={course} isEnrolled={false} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('courses.enrolledAll', "You've enrolled in all courses!")}</h3>
                <p className="text-muted-foreground">{t('courses.checkBack', 'Check back later for new content')}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Certificate Preview Modal */}
      <CertificatePreviewModal
        open={certificateModal.open}
        onClose={() => setCertificateModal({ open: false, enrollment: null, courseName: '' })}
        enrollment={certificateModal.enrollment}
        courseName={certificateModal.courseName}
        userName={user?.name || 'Learner'}
      />
    </DashboardLayout>
  );
};

export default Courses;
